import { ReactElement, useEffect, useRef, ReactNode } from 'react';
import IconWrapper from '@components/ui/Icons';
import cn from 'classnames';

import { AccordionUtil } from './AccordionUtil';

import styles from './Accordion.module.scss';

export const Title = ({ children }: { children: ReactNode }): ReactElement => (
  <>{children}</>
);

export const Collapsible = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => <>{children}</>;

export interface AccordionProps {
  children: [ReactElement, ReactElement];
  isOpen?: boolean;
  classNames?: string;
  onStateChange?: (state: 'open' | 'closed') => void;
}

export const Container = ({
  children,
  isOpen = false,
  classNames,
  onStateChange,
}: AccordionProps): ReactElement => {
  let [title, collapsible] = children;
  if (title.type === Collapsible || collapsible.type === Title) {
    [title, collapsible] = [collapsible, title];
  }

  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    let accordion: AccordionUtil;

    if (
      detailsRef?.current &&
      typeof detailsRef.current.animate === 'function'
    ) {
      accordion = new AccordionUtil(detailsRef.current, onStateChange);
    }

    return () => accordion?.destroy();
  }, [onStateChange]);

  return (
    <details className={styles.borderTop} ref={detailsRef} open={isOpen}>
      <summary className={styles.hitArea}>
        <span className={styles.flexContainer}>
          <div className={cn(styles.titleWrapper, classNames)}>{title}</div>
          <div className={styles.iconWrapper}>
            <IconWrapper
              classNames={cn(styles.isClosed, styles.icon)}
              icon="plus"
              width={18}
              height={18}
            />
            <IconWrapper
              classNames={cn(styles.isOpen, styles.icon)}
              icon="minus"
              width={18}
              height={18}
            />
          </div>
        </span>
      </summary>
      <div>{collapsible}</div>
    </details>
  );
};
